import { CURRICULUM } from "training/curriculum";
import { MODULES } from "training/curriculum/modules";
import { getTourSession } from "training/engine/session";
import { QUIZ_PATH } from "training/paths";
import { ITourStep, TourModule } from "training/types";
import {
  IMyTrainingRecord,
  IQuizAnswer,
  ITrainingProgress,
} from "types/Training";

export { CERTIFICATE_PATH, moduleQuizPath, QUIZ_PATH } from "training/paths";

/**
 * CLE-3015 — the small rules the quizzes, the certificate and the banner share. Since John's review each module
 * ends in a three-question quiz that unlocks the next module, and the knowledge check opens once all six are
 * passed. The API enforces both; these rules let the pages say so before asking it.
 */

/** The six modules, in order. */
export const TOUR_MODULES: TourModule[] = [1, 2, 3, 4, 5, 6];

/** What the module and knowledge-check rules read from GET /user/me/training. */
type ITrainingState = Pick<
  IMyTrainingRecord,
  "curriculumVersion" | "trainingProgress"
>;

/** The saved progress, when it was saved under the API's curriculum version (older progress restarts the tour). */
export function currentProgress(
  record: ITrainingState | null | undefined
): ITrainingProgress | null {
  const progress = record?.trainingProgress;
  if (!record || !progress || progress.version !== record.curriculumVersion) {
    return null;
  }
  return progress;
}

/**
 * The modules whose quiz is passed: the saved record's `modules` (under the current curriculum version only)
 * plus the passes noted in this browser session, which the cached record may not show yet.
 */
export function passedModules(
  record: ITrainingState | null | undefined,
  sessionPassed: number[] = getTourSession().passedModules
): Set<number> {
  const passed = new Set<number>(sessionPassed);
  const modules = currentProgress(record)?.modules ?? {};
  TOUR_MODULES.forEach((module) => {
    if (modules[String(module)]?.passedAt) passed.add(module);
  });
  return passed;
}

/** The lowest module before `before` (all six by default) whose quiz is not passed, or null. */
export function firstUnpassedModule(
  passed: Set<number>,
  before: number = TOUR_MODULES.length + 1
): TourModule | null {
  return TOUR_MODULES.find((m) => m < before && !passed.has(m)) ?? null;
}

/** All six module quizzes are passed: the knowledge check is what is left. */
export function isReadyForQuiz(
  record: ITrainingState | null | undefined,
  sessionPassed?: number[]
): boolean {
  return firstUnpassedModule(passedModules(record, sessionPassed)) === null;
}

/** The trainee has begun: progress under this version with a place in the tour, or a module quiz tried. */
export function hasStartedTraining(
  record: ITrainingState | null | undefined
): boolean {
  const progress = currentProgress(record);
  if (!progress) return false;
  return (
    !!progress.currentStepId ||
    (progress.completed ?? []).length > 0 ||
    (progress.skipped ?? []).length > 0 ||
    Object.keys(progress.modules ?? {}).length > 0
  );
}

export type BannerAction =
  | { kind: "start"; label: string }
  | { kind: "resume"; label: string; module: TourModule }
  | { kind: "quiz"; label: string };

/** The banner's button: Start → "Resume Module N" → "Take the knowledge check" once Module 6 is passed. */
export function bannerAction(
  record: ITrainingState | null | undefined,
  sessionPassed?: number[]
): BannerAction {
  const next = firstUnpassedModule(passedModules(record, sessionPassed));
  if (next === null) {
    return { kind: "quiz", label: "Take the knowledge check" };
  }
  if (next === 1 && !hasStartedTraining(record)) {
    return { kind: "start", label: "Start" };
  }
  return { kind: "resume", label: `Resume Module ${next}`, module: next };
}

/**
 * Where the banner's button goes when nothing overrides it: the knowledge check, or the tour on the hospital's
 * Estimates tab (`?training=1` starts it). The banner and the My Account training card both use it, so the
 * two cannot send a trainee to different places.
 */
export function trainingActionPath(
  action: BannerAction,
  accountId: string | undefined
): string {
  if (action.kind === "quiz") return QUIZ_PATH;
  return `/hospitals/view/${accountId}/orders?training=1`;
}

/**
 * Where an uncertified trainee is, in words: "Module 3 of 6", "final knowledge check next", or null when they
 * have not started (older-version progress included, since the tour restarts).
 */
export function trainingProgressLabel(action: BannerAction): string | null {
  if (action.kind === "quiz") return "final knowledge check next";
  if (action.kind === "resume") {
    return `Module ${action.module} of ${TOUR_MODULES.length}`;
  }
  return null;
}

export interface IWhereItLives {
  module: TourModule;
  moduleTitle: string;
  /** The step's heading (its `title`, else its on-screen label), or the first sentence of its text. */
  what: string;
}

/** The first sentence of the step's first paragraph. */
export function firstSentence(text: string): string {
  const paragraph = text.split("\n").find((line) => line.trim()) ?? "";
  const match = /^.*?[.!?](?=\s|$)/.exec(paragraph.trim());
  return (match ? match[0] : paragraph).trim();
}

/** "Where it lives" for a wrong answer: the curriculum step the API names, or null for an unknown id. */
export function whereItLives(
  stepId: string,
  steps: ITourStep[] = CURRICULUM
): IWhereItLives | null {
  const step = steps.find((s) => s.id === stepId);
  if (!step) return null;
  return {
    module: step.module,
    moduleTitle: MODULES[step.module].title,
    what: step.title ?? step.label ?? firstSentence(step.text),
  };
}

/**
 * "Try again": keep the answers that were right, clear the wrong ones. Returns the answers to keep,
 * keyed by question id.
 */
export function keepRightAnswers(
  selected: Record<string, number>,
  wrongIds: string[]
): Record<string, number> {
  const kept = { ...selected };
  wrongIds.forEach((id) => {
    delete kept[id];
  });
  return kept;
}

export function toAnswers(selected: Record<string, number>): IQuizAnswer[] {
  return Object.entries(selected).map(([questionId, choiceIndex]) => ({
    questionId,
    choiceIndex,
  }));
}
